import React, { ReactElement, useCallback, useState } from "react";
import { Box, Step, StepLabel, Stepper } from "@mui/material";
import { VerticalBox } from "src/components/common/box/VerticalBox.tsx";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { AavoWizardStep, AavoWizardStepProps } from "./AavoWizardStep.tsx";
import { AavoWizardStepContext } from "./AavoWizardContext.ts";
import { faAngleLeft, faAngleRight, faSave, faTimes } from "@fortawesome/pro-regular-svg-icons";
import i18n from "i18next";

export interface AavoWizardProps {
	children: React.ReactNode;
	onSubmit: () => Promise<unknown>;
	onCancel: () => void;
	onBeforeNext?: (currentStepIndex: number) => void | Promise<void>;
	/** Override the save button label (default: i18n.t("save")). */
	submitLabel?: string;
	/** Called when any step form is edited. Used for dirty-state tracking in dialogs. */
	onFormEdited?: () => void;
}

export const AavoWizard = ({
	children,
	onSubmit,
	onCancel,
	onBeforeNext,
	submitLabel,
	onFormEdited,
}: AavoWizardProps) => {
	// Extract AavoWizardStep children.
	// React.Children.toArray flattens and filters out null/false (conditional steps).
	const steps = React.Children.toArray(children).filter(
		(child): child is ReactElement<AavoWizardStepProps> =>
			React.isValidElement(child) && child.type === AavoWizardStep,
	);

	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	// Steps default to valid until they mount and report their actual state.
	const [validationState, setValidationState] = useState<Record<number, boolean>>({});

	const isFirstStep = currentStepIndex === 0;
	const isLastStep = currentStepIndex === steps.length - 1;
	const currentStepValid = validationState[currentStepIndex] ?? true;
	const anyStepInvalid = Object.values(validationState).some((v) => !v);

	const handleValidationChange = useCallback(
		(isValid: boolean) => {
			setValidationState((prev) => {
				if (prev[currentStepIndex] === isValid) return prev;
				return { ...prev, [currentStepIndex]: isValid };
			});
		},
		[currentStepIndex],
	);

	const activeStep = steps[currentStepIndex];

	return (
		<VerticalBox sx={{ flex: 1, padding: 1, gap: 1 }}>
			<VerticalBox sx={{ flex: 1, gap: 2 }}>
				{steps.length > 1 && (
					<Stepper activeStep={currentStepIndex}>
						{steps.map((step, i) => (
							<Step key={i} completed={false}>
								<StepLabel>{step.props.label}</StepLabel>
							</Step>
						))}
					</Stepper>
				)}

				{/* Key forces remount when switching steps, ensuring clean form state. */}
				<AavoWizardStepContext.Provider
					key={currentStepIndex}
					value={{ onValidationChange: handleValidationChange, onFormEdited }}
				>
					{activeStep?.props.children}
				</AavoWizardStepContext.Provider>
			</VerticalBox>

			<HorizontalBox sx={{ gap: 2, justifyContent: "flex-end", paddingRight: 1 }}>
				<Box sx={{ flex: 1 }} />
				<AsyncButton onClick={onCancel} label={i18n.t("cancel")} icon={faTimes} />
				{!isFirstStep && (
					<AsyncButton
						onClick={() => setCurrentStepIndex((i) => i - 1)}
						label={i18n.t("back")}
						icon={faAngleLeft}
					/>
				)}
				{isLastStep && (
					<AsyncButton
						onClick={onSubmit}
						label={submitLabel ?? i18n.t("save")}
						icon={faSave}
						disabled={anyStepInvalid}
					/>
				)}
				{!isLastStep && (
					<AsyncButton
						variant="contained"
						onClick={handleNext}
						label={i18n.t("next")}
						icon={faAngleRight}
						disabled={currentStepValid ? false : i18n.t("please_fix_validation_errors_before_saving")}
					/>
				)}
			</HorizontalBox>
		</VerticalBox>
	);

	async function handleNext() {
		await onBeforeNext?.(currentStepIndex);
		setCurrentStepIndex((i) => i + 1);
	}
};
